/* =========================================================================
   HOLY FOREST — SEITENVERHALTEN
   Kopfzeile, Scroll-Reveal, mobile Kaufleiste, Cookie-Leiste.

   Die Warenkorb-Schublade hat einen eigenen Besitzer:
   assets/holyforest-cart-extras.js. Beide Dateien sind klassische Skripte im
   selben globalen Gueltigkeitsbereich — eine doppelte const-Deklaration
   wuerde BEIDE Dateien mit einem SyntaxError killen. Deshalb keine
   gemeinsamen Namen und keine Kopien.
   ========================================================================= */

let hfScrollFrame;

const hfUpdateHeader = () => {
  document.documentElement.classList.toggle('hf-scrolled', window.scrollY > 12);
  hfScrollFrame = undefined;
};

/* --- Scroll-Reveal --------------------------------------------------------
   Nur innere Elemente, nie .shopify-section: ein transform auf der Sektion
   erzeugt einen containing block und bricht position:fixed (Mobile-Buybar).
   Das Verstecken passiert in CSS und greift erst ab 750 px. */
const HF_REVEAL_GROUPS = [
  '.hf-product-world__header',
  '.hf-product-world__card',
  '.hf-compare__header',
  '.hf-compare__card',
  '.hf-sets__header',
  '.hf-sets__card',
  '.hf-making-story__media',
  '.hf-making-story__content',
  '.hf-guide-stories__header',
  '.hf-guide-stories__card',
  '.hf-trust-panel__intro',
  '.hf-trust-panel__fact',
  '.hf-cta__lead',
  '.hf-cta__aside'
];

const hfRevealAll = () => {
  document.querySelectorAll('.hf-reveal').forEach((el) => el.classList.add('is-in'));
};

const hfMountReveals = () => {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );

  HF_REVEAL_GROUPS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el, index) => {
      if (el.classList.contains('hf-reveal')) return;
      el.classList.add('hf-reveal');
      el.style.setProperty('--hf-reveal-delay', `${Math.min(index, 3) * 90}ms`);
      observer.observe(el);
    });
  });

  window.setTimeout(hfRevealAll, 3000);
};

const hfMountMobilePurchase = () => {
  if (!document.body.classList.contains('template-product')) return;
  if (document.querySelector('.hf-mobile-buybar')) return;

  const productInfo = document.querySelector('.product__info-container');
  const originalButton = productInfo?.querySelector('.product-form__submit');
  const titleSource = document.querySelector('.product__title h1');
  const priceSource = productInfo?.querySelector('[id^="price-"] .price');
  if (!originalButton || !titleSource || !priceSource) return;

  const getPriceText = () => {
    const priceRoot = productInfo.querySelector('[id^="price-"] .price');
    if (!priceRoot) return '';

    const priceItem = priceRoot.classList.contains('price--on-sale')
      ? priceRoot.querySelector('.price__sale .price-item--sale')
      : priceRoot.querySelector('.price__regular .price-item--regular');

    return (priceItem || priceRoot.querySelector('.price-item'))?.textContent.replace(/\s+/g, ' ').trim() || '';
  };

  const bar = document.createElement('aside');
  const summary = document.createElement('div');
  const title = document.createElement('p');
  const price = document.createElement('p');
  const button = document.createElement('button');

  bar.className = 'hf-mobile-buybar';
  bar.setAttribute('aria-label', titleSource.textContent.trim());
  summary.className = 'hf-mobile-buybar__summary';
  title.className = 'hf-mobile-buybar__title';
  price.className = 'hf-mobile-buybar__price';
  button.className = 'hf-mobile-buybar__button';
  button.type = 'button';
  title.textContent = titleSource.textContent.trim();
  price.textContent = getPriceText();

  const syncPurchase = () => {
    const currentButton = productInfo.querySelector('.product-form__submit');
    const currentPrice = getPriceText();
    if (!currentButton || !currentPrice) return;

    const buttonLabel = currentButton.textContent.replace(/\s+/g, ' ').trim();
    button.disabled = currentButton.disabled;
    button.textContent = buttonLabel.replace(/\s+legen$/i, '');
    button.setAttribute('aria-label', currentButton.getAttribute('aria-label') || buttonLabel);
    price.textContent = currentPrice;
  };

  syncPurchase();
  button.addEventListener('click', () => productInfo.querySelector('.product-form__submit')?.click());
  new MutationObserver(syncPurchase).observe(productInfo, {
    attributes: true,
    childList: true,
    characterData: true,
    subtree: true
  });

  summary.append(title, price);
  bar.append(summary, button);
  document.body.append(bar);

  const setPurchaseVisibility = (visible) => {
    bar.classList.toggle('is-visible', visible);
    document.body.classList.toggle('hf-mobile-buy-active', visible);
  };

  let purchaseFrame;
  const updatePurchaseVisibility = () => {
    if (purchaseFrame) return;
    purchaseFrame = window.requestAnimationFrame(() => {
      /* Immer NEU suchen, nicht die beim Start gemerkte Schaltflaeche nehmen.
         Dawn ersetzt .product-form__submit, sobald die Produktinfo neu
         gerendert wird (Variantenwechsel). Eine gemerkte Referenz haengt
         danach ausserhalb des Dokuments; ihr Rechteck ist 0/0/0/0, bottom
         also 0 — und 0 < 0 ist falsch, die Leiste bliebe fuer immer weg.
         syncPurchase() sucht aus genau diesem Grund schon immer neu; diese
         Stelle war die einzige, die es nicht tat. `|| originalButton` haelt
         das Verhalten identisch, solange die Referenz noch gilt.

         NICHT NACHGEMESSEN: in der Browser-Vorschau laesst sich das nicht
         pruefen. Ist das Vorschaufenster zugeklappt, meldet die Seite
         document.hidden = true, und dann laeuft requestAnimationFrame gar
         nicht — die Leiste kann in der Vorschau grundsaetzlich nicht
         erscheinen. Am echten Geraet gegenpruefen. */
      const aktuelleSchaltflaeche = productInfo.querySelector('.product-form__submit') || originalButton;
      setPurchaseVisibility(aktuelleSchaltflaeche.getBoundingClientRect().bottom < 0);
      purchaseFrame = undefined;
    });
  };

  window.addEventListener('scroll', updatePurchaseVisibility, { passive: true });
  window.addEventListener('resize', updatePurchaseVisibility);
  updatePurchaseVisibility();
};

const hfNormalizeCookieConsent = () => {
  const banners = [...document.querySelectorAll('.cc-window[aria-label="cookieconsent"]')];
  if (!banners.length) return;

  banners.slice(0, -1).forEach((banner) => banner.remove());

  const activeBanner = banners.at(-1);
  const privacyLink = activeBanner.querySelector('.cc-link');
  if (privacyLink) {
    privacyLink.href = '/policies/privacy-policy';
    privacyLink.removeAttribute('target');
  }

  const acceptButton = activeBanner.querySelector('.cc-dismiss');
  if (acceptButton && !acceptButton.dataset.hfConsentBound) {
    acceptButton.dataset.hfConsentBound = 'true';
    acceptButton.addEventListener(
      'click',
      () => {
        const hideBanner = () => activeBanner.remove();
        const privacyApi = window.Shopify?.customerPrivacy;

        if (privacyApi?.setTrackingConsent) {
          privacyApi.setTrackingConsent(true, hideBanner);
          return;
        }

        if (window.Shopify?.loadFeatures) {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (!error && window.Shopify.customerPrivacy?.setTrackingConsent) {
              window.Shopify.customerPrivacy.setTrackingConsent(true, hideBanner);
              return;
            }
            hideBanner();
          });
          return;
        }

        hideBanner();
      },
      { once: true }
    );
  }
};

const hfWatchCookieConsent = () => {
  hfNormalizeCookieConsent();
  new MutationObserver(hfNormalizeCookieConsent).observe(document.body, {
    childList: true,
    subtree: true
  });
};

window.addEventListener(
  'scroll',
  () => {
    if (hfScrollFrame) return;
    hfScrollFrame = window.requestAnimationFrame(hfUpdateHeader);
  },
  { passive: true }
);

document.addEventListener('DOMContentLoaded', () => {
  hfUpdateHeader();
  hfMountReveals();
  hfMountMobilePurchase();
  hfWatchCookieConsent();
});

document.addEventListener('shopify:section:load', () => {
  hfMountReveals();
  hfMountMobilePurchase();
});
